/**
 * SignInButton — Top-bar affordance. Renders null when no report PIN is
 * configured (so unprotected workshops don't see a dangling button).
 * Otherwise:
 *   - not authed → "Kirjaudu" → opens SignInPinModal
 *   - authed     → "Kirjautunut" with a check icon, click to sign out
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Lock, Check } from 'lucide-react';
import { useReportAuth } from './ReportAuthContext';
import SignInPinModal from './SignInPinModal';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${p => p.$authed ? 'rgba(22, 163, 74, 0.18)' : 'rgba(255, 255, 255, 0.12)'};
  color: ${p => p.$authed ? '#15803d' : '#fff'};
  border: 1px solid ${p => p.$authed ? 'rgba(22, 163, 74, 0.55)' : 'rgba(255, 255, 255, 0.25)'};
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: ${p => p.$authed ? 'rgba(22, 163, 74, 0.28)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

export default function SignInButton() {
  const { pinRequired, isAuthed, accountAuthed, signOut } = useReportAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // No PIN configured → no button. Avoids dangling lock affordances on
  // workshops that are deliberately public.
  if (!pinRequired) return null;
  // A sign-in that opens the report (#1475) signs out from the avatar
  // — a separate "Kirjautunut" pill next to the avatar is just noise.
  if (accountAuthed) return null;

  if (isAuthed) {
    return (
      <Pill $authed onClick={signOut} title={t('report.topbar.signOutTitle')}>
        <Check size={14} strokeWidth={2.4} />
        {t('report.topbar.signedIn')}
      </Pill>
    );
  }

  return (
    <>
      <Pill onClick={() => setOpen(true)} title={t('report.topbar.signInTitle')}>
        <Lock size={14} strokeWidth={2.2} />
        {t('report.topbar.signIn')}
      </Pill>
      <SignInPinModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
